import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Tab } from "@/types";
import { cn } from "@/lib/utils";

interface EditTabModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tab: Tab;
  onSave: (updated: Partial<Tab> & { favicon_url?: string }) => void;
}

const EditTabModal: React.FC<EditTabModalProps> = ({ open, onOpenChange, tab, onSave }) => {
  const [title, setTitle] = useState(tab.title);
  const [url, setUrl] = useState(tab.url);
  const [image, setImage] = useState<string | undefined>(tab.favicon_url);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImage(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    let favicon_url = image;
    // If a new file is selected, it's already base64
    onSave({
      tab_id: tab.tab_id,
      title,
      url,
      favicon_url,
    });
    setLoading(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Tab</DialogTitle>
          <DialogDescription>Edit the title, URL, and image for this tab.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSave();
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL</label>
            <input
              type="url"
              className="w-full border rounded px-3 py-2"
              value={url}
              onChange={e => setUrl(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Image</label>
            <div className="flex items-center gap-3">
              {image && (
                <img src={image} alt="Favicon" className="w-10 h-10 rounded object-contain border" />
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              <button
                type="button"
                className="px-3 py-1 rounded bg-accent text-foreground border hover:bg-accent/70"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose Image
              </button>
            </div>
          </div>
          <DialogFooter>
            <button
              type="submit"
              className={cn("px-4 py-2 rounded bg-hintword-orange text-white font-semibold", loading && "opacity-60 cursor-not-allowed")}
              disabled={loading}
            >
              Save
            </button>
            <DialogClose asChild>
              <button type="button" className="px-4 py-2 rounded border">Cancel</button>
            </DialogClose>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTabModal; 